import { useEffect, useId, useRef, useState } from 'react'
import { CataloguePicker } from '@/lib/programs'
import { Icon } from '@/components/Icon'

export interface ReviewFiltersValue {
  kind: string
  program: string
}

/** Both filters live in one small popover; selection is local to the current queue visit. */
export function ReviewFilters({
  value,
  onChange,
  science = false,
}: {
  value: ReviewFiltersValue
  onChange: (value: ReviewFiltersValue) => void
  science?: boolean
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const id = useId()
  const count = Number(!!value.kind) + Number(!!value.program)
  useEffect(() => {
    if (!open) return
    panel.current?.querySelector<HTMLButtonElement>('button')?.focus()
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', outside)
    return () => document.removeEventListener('pointerdown', outside)
  }, [open])
  function close() {
    setOpen(false)
    trigger.current?.focus()
  }
  return (
    <div
      className="review-filters-popover"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          close()
        }
      }}
    >
      <button
        className="review-filters-trigger"
        type="button"
        ref={trigger}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="filter" size={16} />
        Filters{count > 0 && <span className="review-filter-count">{count}</span>}
      </button>
      {open && (
        <div
          ref={panel}
          id={id}
          className="review-filters-panel"
          role="dialog"
          aria-label="Filter reviews"
        >
          <div className="panel-section-head">
            <strong>Filters</strong>
            <button
              className="link small"
              aria-disabled={!count}
              onClick={() => {
                if (count) onChange({ kind: '', program: '' })
              }}
            >
              Clear all
            </button>
          </div>
          <fieldset>
            <legend>Review type</legend>
            <div className="review-filter-options">
              {[
                ...(!science ? [{ value: 'submission', label: 'Peer review' }] : []),
                { value: 'quality', label: science ? 'Scientific review' : 'Quality check' },
                ...(science ? [{ value: 'scientific', label: 'Physics templates' }] : []),
              ].map((option) => (
                <button
                  type="button"
                  key={option.value}
                  aria-pressed={value.kind === option.value}
                  onClick={() =>
                    onChange({ ...value, kind: value.kind === option.value ? '' : option.value })
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Program</legend>
            <CataloguePicker label="Filter by program" value={value.program ? [value.program] : []}
              onChange={programs => onChange({ ...value, program: programs.at(-1) ?? '' })} />
          </fieldset>
          <div className="review-filters-footer">
            <button className="primary sm" onClick={close}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
